import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  query,
  orderBy
} from "firebase/firestore";
import { toast } from "react-toastify";

const User = () => {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [editingUserId, setEditingUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    designation: "",
    phone: "",
    status: "Active",
  });

  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const usersCollection = collection(db, "users");
      const usersQuery = query(usersCollection, orderBy("userName"));
      const usersSnapshot = await getDocs(usersQuery);
      const userList = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().userName || "",
        email: doc.data().userEmail || "",
        role: doc.data().userRole || "",
        phone: doc.data().phone || "",
        status: doc.data().status || "Active",
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      }));
      setUsers(userList);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const validateForm = () => {
    // Basic validation
    if (!formData.name.trim()) return "Name is required";
    if (!formData.email.trim()) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return "Email is invalid";
    if (!formData.designation.trim()) return "Role/Designation is required";
    if (!formData.phone.trim()) return "Phone number is required";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const error = validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    setIsLoading(true);
    try {
      if (editingUserId) {
        const userRef = doc(db, "users", editingUserId);
        await updateDoc(userRef, {
          userName: formData.name,
          userEmail: formData.email,
          userRole: formData.designation,
          phone: formData.phone,
          status: formData.status,
          updatedAt: new Date(),
        });
        toast.success("User updated successfully!");
      } else {
        await addDoc(collection(db, "users"), {
          userName: formData.name,
          userEmail: formData.email,
          userRole: formData.designation,
          phone: formData.phone,
          status: formData.status,
          createdAt: new Date(),
        });
        toast.success("User added successfully!");
      }

      await fetchUsers();

      setFormData({
        name: "",
        email: "",
        designation: "",
        phone: "",
        status: "Active",
      });
      setEditingUserId(null);
      setShowModal(false);
    } catch (error) {
      console.error("Error saving user:", error);
      toast.error(editingUserId ? "Failed to update user" : "Failed to add user");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUserId(user.id);
    setFormData({
      name: user.name,
      email: user.email,
      designation: user.role,
      phone: user.phone,
      status: user.status,
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    
    setIsLoading(true);
    try {
      await deleteDoc(doc(db, "users", userToDelete.id));
      toast.success("User deleted successfully!");
      setUserToDelete(null);
      setShowDeleteConfirm(false);
      await fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Failed to delete user");
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setEditingUserId(null);
    setFormData({
      name: "",
      email: "",
      designation: "",
      phone: "",
      status: "Active",
    });
  };

  return (
    <div className="d-flex">
      <Sidebar />
      <div
        className="content"
        style={{
          marginLeft: "280px",
          width: "calc(100% - 280px)",
          transition: "all 0.3s ease",
          padding: "20px",
        }}
      >
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="fw-bold">Users Management</h1>
              <p className="text-muted">
                Manage all system users from here
              </p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
            >
              <i className="bi bi-person-plus"></i> Add New User
            </button>
          </div>

          {/* Search and filters */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <div className="row g-2">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text bg-light">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button 
                        className="btn btn-outline-secondary" 
                        type="button"
                        onClick={() => setSearchTerm("")}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-md-6 text-md-end">
                  <button className="btn btn-outline-secondary me-2" onClick={fetchUsers}>
                    <i className="bi bi-arrow-clockwise"></i> Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white py-3">
              <h5 className="mb-0">Registered Users</h5>
            </div>
            <div className="card-body">
              {isLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading users...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Phone</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map((user, index) => (
                          <tr key={user.id}>
                            <td>{index + 1}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="avatar-container bg-light text-primary rounded-circle me-2 d-flex align-items-center justify-content-center" style={{ width: "40px", height: "40px" }}>
                                  {user.name.charAt(0).toUpperCase()}
                                </div>
                                <div>{user.name}</div>
                              </div>
                            </td>
                            <td>{user.email}</td>
                            <td>{user.role}</td>
                            <td>{user.phone}</td>
                            <td>
                              <span
                                className={`badge ${
                                  user.status === "Active"
                                    ? "bg-success"
                                    : "bg-warning text-dark"
                                }`}
                              >
                                {user.status}
                              </span>
                            </td>
                            <td>
                              <div className="btn-group">
                                <button 
                                  className="btn btn-sm btn-outline-primary"
                                  title="View details"
                                >
                                  <i className="bi bi-eye"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => handleEdit(user)}
                                  title="Edit user"
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => confirmDelete(user)}
                                  title="Delete user"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            {searchTerm ? (
                              <>
                                <div className="text-muted mb-2">No users match your search criteria</div>
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => setSearchTerm("")}>
                                  Clear search
                                </button>
                              </>
                            ) : (
                              <>
                                <div className="text-muted mb-2">No users available</div>
                                <button className="btn btn-sm btn-primary" onClick={() => setShowModal(true)}>
                                  Add your first user
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="card-footer bg-white">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted">
                    Showing {filteredUsers.length} of {users.length} users
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Add/Edit User Modal */}
          {showModal && (
            <div
              className="modal d-block"
              tabIndex="-1"
              role="dialog"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content">
                  <form onSubmit={handleSubmit}>
                    <div className="modal-header">
                      <h5 className="modal-title">
                        {editingUserId ? "Edit User" : "Add New User"}
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowModal(false)}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <div className="mb-3">
                        <label className="form-label">
                          Full Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="name"
                          value={formData.name}
                          placeholder="Enter full name"
                          required
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">
                          Email Address <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          name="email"
                          value={formData.email}
                          placeholder="Enter email address"
                          required
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">
                          Role/Designation <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="designation"
                          value={formData.designation}
                          placeholder="Enter role or designation"
                          required
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">
                          Phone Number <span className="text-danger">*</span>
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          name="phone"
                          value={formData.phone}
                          placeholder="Enter phone number"
                          required
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowModal(false)}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn btn-primary"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                          </>
                        ) : (
                          editingUserId ? "Update User" : "Add User"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div
              className="modal d-block"
              tabIndex="-1"
              role="dialog"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title text-danger">Confirm Delete</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowDeleteConfirm(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <p>Are you sure you want to delete the user <strong>{userToDelete?.name}</strong>?</p>
                    <p className="text-danger"><small>This action cannot be undone.</small></p>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-danger"
                      onClick={handleDelete}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Deleting...
                        </>
                      ) : (
                        "Delete User"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default User;